import { useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { sortBy } from 'lodash-es';
import cn from 'classnames';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { Button, Divider, ExpandableSearchInput, ModalBody, ModalFooter, ModalHeader } from '@ds';
import { Collapse } from '@ui';
import { CaseNoteCard } from '@lp/features/CaseNoteCard';
import { useCaseNotesStore } from '@/store/case-notes.store';
import { CaseNote } from '@/api/apis/case';
import { getSortIcon } from '@/utils/helpers';

export const NotesListModalContent = ({ caseId, notes }: { caseId: string; notes: CaseNote[] }) => {
  const setCreateNote = useCaseNotesStore.use.setCreateNote();
  const setDrawerOpened = useCaseNotesStore.use.setDrawerOpened();
  const [pinnedExpanded, togglePinnedExpanded] = useToggle(false);
  const [sortAsc, setSortAsc] = useState<boolean>();
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSortAsc = () => setSortAsc((val) => !val);

  const pinnedNotes = useMemo(() => notes.filter((n) => n.isPinned), [notes]);

  const notesList = useMemo(() => {
    let list = notes.filter((n) => !n.isPinned);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((n) => n.message.toLowerCase().includes(q));
    }

    const sortedAsc = sortBy(list, (n) => new Date(n.modifiedAt ?? n.createdAt).getTime());
    return sortAsc ? sortedAsc : sortedAsc.slice().reverse();
  }, [notes, searchQuery, sortAsc]);

  return (
    <>
      <ModalHeader>
        <h3>Case Notes</h3>
      </ModalHeader>
      <div className="px-6">
        <Divider />
      </div>
      <ModalBody className="py-6 gap-8">
        {Boolean(pinnedNotes.length) && (
          <div>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={togglePinnedExpanded}>
              <span className="text-l-sb">Pinned Notes</span>
              <MdKeyboardArrowDown size={24} className={cn({ 'rotate-180': pinnedExpanded })} />
            </div>
            <Collapse isExpanded={pinnedExpanded}>
              <div className="grid gap-3 pt-4">
                {pinnedNotes.map((note) => (
                  <CaseNoteCard caseId={caseId} key={note.noteId} data={note} />
                ))}
              </div>
            </Collapse>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={toggleSortAsc}>
              <span className="text-l-sb">Other Case Notes</span>
              <span className="text-base-30">{getSortIcon(sortAsc)}</span>
            </div>
            <div className="flex items-center justify-end w-3/5">
              <ExpandableSearchInput
                onClear={() => setSearchQuery('')}
                placeholder="Search"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
            </div>
          </div>
          <div className="grid gap-3">
            {notesList.map((note) => (
              <CaseNoteCard caseId={caseId} key={note.noteId} data={note} />
            ))}
            {!notesList.length && (
              <div className="flex items-center justify-center py-8">
                <span className="text-base-25 text-2xl font-bold">No results found</span>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <div className="px-6">
        <Divider />
      </div>
      <ModalFooter>
        <div className="flex gap-3">
          <Button variant="outline" onPress={() => setDrawerOpened(false)} className="flex-1">
            Close
          </Button>
          <Button variant="solid" onPress={() => setCreateNote(true)} className="flex-1 text-base-90">
            Add Note
          </Button>
        </div>
      </ModalFooter>
    </>
  );
};
